import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { LookupItem, Paged, RetailCommitResult, RetailOrder, RetailPreview } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { useAuth } from '../auth'
import { Badge, Empty, Notice, Pager, fmtTime } from '../components/ui'

const CLASS_TONE: Record<string, string> = { NEW: 'ok', DUPLICATE: 'warn', DOMAIN_NOT_FOUND: 'muted' }
const CLASS_LABEL: Record<string, string> = { NEW: '可写入', DUPLICATE: '重复（跳过）', DOMAIN_NOT_FOUND: '不在库存' }

const EXAMPLE = `逸影直播
1.www.qtbkm.study
2.orzzz52.top

舒心直播
1.www.rwlvs.study
2.orzzz53.top`

export function Retail() {
  const { user } = useAuth()
  const [buyer, setBuyer] = useState('')
  const [text, setText] = useState('')
  const [preview, setPreview] = useState<RetailPreview | null>(null)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [previewError, setPreviewError] = useState<unknown>(null)
  const [page, setPage] = useState(1)
  const op = useOperation<RetailCommitResult>('retail')

  const { data, error: listError, loading, reload } = useAsync(
    () => api.get<Paged<RetailOrder>>(`/api/retail?page=${page}&size=25`),
    [page],
  )
  const sp = useAsync(() => api.get<{ items: LookupItem[] }>('/api/salespersons'), [])
  const names = sp.data?.items ?? []

  // A deleted name must not stay selected: the server rejects a buyer that is
  // off the list, so the dropdown and the stored choice have to agree.
  useEffect(() => {
    if (buyer && names.length > 0 && !names.some((n) => n.name === buyer)) {
      setBuyer('')
      setPreview(null)
    }
  }, [buyer, names])

  async function doPreview() {
    setPreviewBusy(true)
    setPreviewError(null)
    try {
      // The token binds the buyer too, so any later change invalidates this preview.
      setPreview(await api.post<RetailPreview>('/api/retail/preview', { buyer, text }))
      op.newIntent()
    } catch (e) {
      setPreviewError(e)
      setPreview(null)
    } finally {
      setPreviewBusy(false)
    }
  }

  async function commit() {
    const res = await op.run((key) =>
      api.post<RetailCommitResult>('/api/retail/commit', { buyer, token: preview?.token ?? '', text }, key),
    )
    if (res) {
      setPreview(null)
      setText('')
      reload()
    }
  }

  const rows = data?.items ?? []
  const hasConflicts = !!preview && preview.conflicts > 0
  const canWrite = !!user?.canWrite
  const parseErrors = preview?.parseErrors ?? []

  return (
    <section>
      <header className="page-head compact">
        <h1>下单记录</h1>
        <div className="row">
          <a className="btn" href="/api/export/orders" download>
            导出 CSV
          </a>
          <span className="muted">共 {data?.total ?? 0} 条</span>
        </div>
      </header>

      <div className="grid two">
        <div className="card">
          <h2>粘贴订单</h2>
          <label className="field">
            <span>业务员（下单人）</span>
            <select
              value={buyer}
              onChange={(e) => {
                setBuyer(e.target.value)
                setPreview(null)
              }}
            >
              <option value="">请选择…</option>
              {names.map((n) => (
                <option key={n.id} value={n.name}>
                  {n.name}
                </option>
              ))}
            </select>
            {names.length === 0 && !sp.loading && (
              <em>名单还是空的：先到设置页添加业务员名字，这里才会出现选项。</em>
            )}
          </label>
          <textarea
            rows={10}
            aria-label="下单记录原文，按机构分块粘贴"
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setPreview(null)
            }}
            placeholder={EXAMPLE}
          />
          <div className="row spread">
            <span className="muted">
              一块 = 机构名 + 1号/2号域名；空行或下一个机构名结束一块。引号、<code>1.</code> 编号会被忽略。
            </span>
            <div className="row">
              <button
                type="button"
                onClick={() => {
                  setText('')
                  setPreview(null)
                }}
                disabled={!text}
              >
                清空
              </button>
              <button
                type="button"
                className="primary"
                onClick={doPreview}
                disabled={!buyer || !text.trim() || previewBusy || names.length === 0}
              >
                {previewBusy ? '解析中…' : '预览 / 查重'}
              </button>
            </div>
          </div>
          {names.length === 0 ? (
            <p className="sub">业务员名单是空的：先到设置页添加名字，才能预览。</p>
          ) : !buyer ? (
            <p className="sub">先选业务员，「预览 / 查重」才能点。</p>
          ) : !text.trim() ? (
            <p className="sub">先粘贴订单内容，「预览 / 查重」才能点。</p>
          ) : null}
          <Notice error={previewError} />
          <Notice error={sp.error} />
          {parseErrors.length > 0 && (
            <div className="notice warn">
              <strong>有 {parseErrors.length} 处格式错误，提交前必须修正（不会猜测写入）</strong>
              <ul className="errs">
                {parseErrors.slice(0, 20).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="card">
          <h2>预览结果</h2>
          {!preview && <p className="muted">选业务员、粘贴内容后点「预览 / 查重」。</p>}
          {preview && (
            <>
              <div className="row wrap counts">
                <Badge tone="muted">{preview.buyer}</Badge>
                <Badge tone="ok">写入 {preview.insert}</Badge>
                <Badge tone="warn">重复跳过 {preview.skip}</Badge>
                <Badge tone={hasConflicts ? 'warn' : 'muted'}>不在库存 {preview.conflicts}</Badge>
              </div>
              {hasConflicts && (
                <div className="notice warn">
                  有 {preview.conflicts} 行的域名在库存里找不到，那一格的收录时间会留空。照常收录，不影响库存。
                </div>
              )}
              <table className="table compact">
                <thead>
                  <tr>
                    <th>机构</th>
                    <th>1号域名</th>
                    <th>2号域名</th>
                    <th>收录时间</th>
                    <th>分类</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows ?? []).slice(0, 200).map((r, i) => (
                    <tr key={i} className={r.class === 'NEW' ? '' : 'dim'}>
                      <td>{r.line.institution}</td>
                      <td className="mono small">
                        {r.line.domainOne || '-'}
                        {!r.line.matchOne?.found && r.line.domainOne ? <span className="tag">不在库存</span> : null}
                      </td>
                      <td className="mono small">
                        {r.line.domainTwo || '-'}
                        {!r.line.matchTwo?.found && r.line.domainTwo ? <span className="tag">不在库存</span> : null}
                      </td>
                      <td>{r.line.orderDate || '自动为空'}</td>
                      <td>
                        <Badge tone={CLASS_TONE[r.class]}>{CLASS_LABEL[r.class] ?? r.class}</Badge>
                        {r.warnings?.map((w) => (
                          <div key={w} className="sub">
                            {w}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="row end">
                <button
                  type="button"
                  className="primary"
                  disabled={!canWrite || preview.insert === 0 || parseErrors.length > 0 || op.busy}
                  onClick={() => void commit()}
                >
                  {op.busy ? '收录中…' : `收录 ${preview.insert} 条`}
                </button>
              </div>
              {!canWrite && <p className="sub">当前账号是只读角色，不能写入下单记录。</p>}
              <Notice error={op.error} />
            </>
          )}
          {op.result && !preview && (
            <div className="notice ok">
              <strong>
                写入 {op.result.inserted} 条，跳过 {op.result.skipped} 条（业务员 {op.result.buyer}）
              </strong>
              <div className="sub">批次 #{op.result.batchId} · 请求 {op.result.batchCode}</div>
            </div>
          )}
        </div>
      </div>

      <div className="card table-card">
        <h2>已录入下单记录</h2>
        <Notice error={listError} />
        {!loading && rows.length === 0 && <Empty text="还没有下单记录。" />}
        {rows.length > 0 && (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>收录时间</th>
                  <th>业务员</th>
                  <th>机构</th>
                  <th>1号域名</th>
                  <th>2号域名</th>
                  <th>库存核对</th>
                  <th>录入</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td>{o.orderDate || '-'}</td>
                    <td>{o.buyer}</td>
                    <td>{o.institution || '-'}</td>
                    <td className="mono">{o.domainOne || '-'}</td>
                    <td className="mono">{o.domainTwo || '-'}</td>
                    <td>
                      {o.domainOneEverSold || o.domainTwoEverSold ? (
                        <Badge tone="sold">履历有售出</Badge>
                      ) : (
                        <Badge tone="muted">履历无售出</Badge>
                      )}
                      {o.conflictAck && <span className="tag">已确认差异</span>}
                    </td>
                    <td className="muted">{fmtTime(o.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager page={page} size={25} total={data?.total ?? 0} onPage={setPage} />
          </>
        )}
      </div>
    </section>
  )
}
